import axios from 'axios'
import { API_BASE_URL } from '@/config/api'

// Utiliser API_BASE_URL + /api pour l'authentification
console.log('🔐 Auth Store - API URL:', `${API_BASE_URL}/api`)

// Créer une instance axios dédiée pour l'authentification
const authAPI = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Intercepteur pour ajouter le token
authAPI.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const state = {
  token: localStorage.getItem('token') || null,
  user: null,
  isAuthenticated: false
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    state.isAuthenticated = !!token
    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
  },
  SET_USER(state, user) {
    state.user = user
  },
  LOGOUT(state) {
    state.token = null
    state.user = null
    state.isAuthenticated = false
    localStorage.removeItem('token')
  }
}

const actions = {
  async login({ commit }, credentials) {
    try {
      console.log('🔑 Attempting login to:', `${API_BASE_URL}/api/auth/login`)
      const response = await authAPI.post('/auth/login', credentials)
      const { token, user } = response.data
      
      commit('SET_TOKEN', token)
      commit('SET_USER', user)
      
      return { success: true, user }
    } catch (error) {
      console.error('❌ Login error:', error.message, error.response?.data)
      const data = error.response?.data
      if (data && data.requiresValidation) {
        const err = new Error(data.message)
        err.requiresValidation = true
        err.email = data.email
        throw err
      }
      const message = data?.message || 'Erreur de connexion'
      throw new Error(message)
    }
  },

  async register({ commit }, userData) {
    try {
      const response = await authAPI.post('/auth/register', userData)
      const { token, user } = response.data
      
      commit('SET_TOKEN', token)
      commit('SET_USER', user)
      
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'inscription'
      throw new Error(message)
    }
  },

  async signup({ commit }, signupData) {
    try {
      const response = await authAPI.post('/auth/signup', signupData)
      return response.data // Retourne { requiresValidation, email }
    } catch (error) {
      console.error('❌ Signup error:', error.message, error.response?.data)
      const message = error.response?.data?.message || 'Erreur lors de la création du compte'
      throw new Error(message)
    }
  },

  async verifyOtp({ commit }, { email, otpCode }) {
    try {
      const response = await authAPI.post('/auth/verify-otp', { email, otpCode })
      const { token, user } = response.data
      
      commit('SET_TOKEN', token)
      commit('SET_USER', user)
      
      return { success: true, user }
    } catch (error) {
      console.error('❌ Verify OTP error:', error.message, error.response?.data)
      const message = error.response?.data?.message || 'Code OTP incorrect ou expiré'
      throw new Error(message)
    }
  },

  async forgotPassword({ commit }, { email }) {
    try {
      const response = await authAPI.post('/auth/forgot-password', { email })
      return response.data
    } catch (error) {
      console.error('❌ Forgot password error:', error.message, error.response?.data)
      const message = error.response?.data?.message || 'Erreur lors de la demande de réinitialisation'
      throw new Error(message)
    }
  },

  async resetPassword({ commit }, { email, otpCode, newPassword }) {
    try {
      const response = await authAPI.post('/auth/reset-password', { email, otpCode, newPassword })
      return response.data
    } catch (error) {
      console.error('❌ Reset password error:', error.message, error.response?.data)
      const message = error.response?.data?.message || 'Erreur lors de la réinitialisation du mot de passe'
      throw new Error(message)
    }
  },

  async loadUser({ commit, state }) {
    if (!state.token) {
      throw new Error('Aucun token disponible')
    }

    try {
      const response = await authAPI.get('/auth/me')
      const { user } = response.data
      
      commit('SET_USER', user)
      commit('SET_TOKEN', state.token) // Réactiver l'état authentifié
      
      return user
    } catch (error) {
      commit('LOGOUT')
      throw new Error('Token invalide')
    }
  },

  async updateProfile({ commit }, profileData) {
    try {
      const response = await authAPI.put('/auth/profile', profileData)
      const { user } = response.data
      
      commit('SET_USER', user)
      
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour'
      throw new Error(message)
    }
  },

  logout({ commit }) {
    commit('LOGOUT')
  }
}

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  user: state => state.user,
  token: state => state.token,
  isAdmin: state => state.user && state.user.role === 'Admin',
  userRole: state => state.user ? state.user.role : null
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
